import { useEffect, useState } from "react";
import { CaptchaPreference } from "../marketing/CaptchaPreference";
import { setCaptchaConsent } from "../marketing/captcha-consent";
import { useLocation } from "react-router";
import {
  denyAnalyticsConsent,
  grantAnalyticsConsent,
  readAnalyticsConsent,
} from "./consent";
import { visitAnalyticsPage } from "./configured-analytics";
import { analyticsUrl } from "./page-context";
import type { SiteSettings } from "@diality/contracts";

export function AnalyticsPageViews({
  initialConsent,
  settings,
}: {
  /**
   * The visitor's consent as resolved by the server (from the request's
   * cookie). When provided, the very first render — including the SSR'd HTML
   * — reflects their choice, so a returning visitor never sees the banner
   * flash. When omitted (e.g. the CMS canvas), the cookie is read after
   * mount instead.
   */
  initialConsent?: boolean | null;
  settings: SiteSettings | null;
}) {
  const { pathname, search } = useLocation();
  const [consented, setConsented] = useState(initialConsent ?? false);
  const [choiceMade, setChoiceMade] = useState(initialConsent != null);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    if (initialConsent !== undefined) return; // the server already knew
    const choice = readAnalyticsConsent(document.cookie);
    const granted = choice === true;
    setChoiceMade(choice !== null);
    setConsented(granted);
  }, [initialConsent]);

  useEffect(() => {
    visitAnalyticsPage(settings, {
      path: pathname,
      location: analyticsUrl(pathname + search, window.location.origin),
      title: document.title,
      referrer: analyticsUrl(document.referrer),
    });
  }, [pathname, search, settings]);

  if (choiceMade && !preferencesOpen) {
    return (
      <button
        type="button"
        className="pointer-events-auto flex min-w-0 shrink items-center gap-4 rounded-[15px] border border-[rgba(49,62,72,0.18)] bg-background px-6 py-4 text-left shadow-lg hover:border-[rgba(49,62,72,0.3)] focus-visible:border-[rgba(49,62,72,0.3)]"
        onClick={() => setPreferencesOpen(true)}
      >
        <span className="cookie-preferences-label min-w-0 flex-1">
          COOKIE PREFERENCES
        </span>
        <span className="product-disclaimer__icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M3 3L15 15M15 3L3 15"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
            />
          </svg>
        </span>
      </button>
    );
  }

  return (
    <section
      aria-label="Cookie consent"
      className="pointer-events-auto w-fit min-w-0 max-w-[calc(100vw-2rem)] shrink rounded-lg border bg-background p-4 shadow-lg"
    >
      <p className="mb-3 text-sm leading-5">
        We use analytics cookies to understand how visitors use this site.
      </p>
      {preferencesOpen && (
        <div className="mb-3"><CaptchaPreference /></div>
      )}
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          className="min-w-0 rounded-md bg-primary px-4 py-2 text-sm leading-5 font-medium text-primary-foreground"
          onClick={() => {
            grantAnalyticsConsent();
            setConsented(true);
            setChoiceMade(true);
            setPreferencesOpen(false);
          }}
        >
          {preferencesOpen && consented
            ? "Keep analytics enabled"
            : "Accept analytics"}
        </button>
        <button
          type="button"
          className="min-w-0 rounded-md border px-4 py-2 text-sm leading-5 font-medium"
          onClick={() => {
            denyAnalyticsConsent();
            setCaptchaConsent(false);
            setConsented(false);
            setChoiceMade(true);
            setPreferencesOpen(false);
          }}
        >
          {preferencesOpen && consented ? "Withdraw all consent" : "Reject all"}
        </button>
      </div>
    </section>
  );
}
